import {
  Button,
  Checkbox,
  CheckboxOnChangeData,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Label,
  makeStyles,
  Textarea,
  TextareaOnChangeData,
} from "@fluentui/react-components";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMassPublishActivityDrafts } from "../../hooks/useActivities";
import { useMassPublishImageDrafts } from "../../hooks/useImages";
import { useMassPublishToolDrafts } from "../../hooks/useTools";
import { ToolTypes } from "../../models/Tool";
import { ClientRoutes } from "../../models/ClientRoutes";
import { useNavigate } from "react-router-dom";
import {
  EntityType,
  getMassPublishTranslationKeys,
  getPublishedItemTitle,
  getResultErrorMessage,
  MassPublishResponse,
} from "../../services/MassPublishService";
import { ButtonWithLoading } from "../ButtonWithLoading";
import {
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
  dialogSurface: {
    width: "400px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
  },
  commentTextArea: {
    minHeight: "150px",
  },
  commentLabel: {
    marginTop: "20px",
  },
  countText: {
    marginBottom: "10px",
    fontWeight: "600",
  },
  resultsContainer: {
    marginTop: "16px",
    maxHeight: "300px",
    overflowY: "auto",
  },
  resultItem: {
    padding: "8px",
    marginBottom: "4px",
    borderRadius: "4px",
    fontSize: "14px",
  },
  successItem: {
    backgroundColor: "#dff6dd",
    color: "#0f5132",
  },
  errorItem: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
  },
  summaryText: {
    fontWeight: "600",
    marginBottom: "12px",
  },
  errorContainer: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "12px",
    borderRadius: "4px",
    marginBottom: "16px",
    border: "1px solid #f5c6cb",
  },
  errorTitle: {
    fontWeight: "600",
    marginBottom: "8px",
  },
  iconWrapper: {
    display: "flex",
    gap: "8px",
  },
  icon: {
    flexShrink: 0,
  },
});

interface MassPublishDialogProps {
  versionIds: string[];
  disabled?: boolean;
  onSuccess?: () => void;
  isQuickEditMode?: boolean;
  entityType?: EntityType;
  toolType?: ToolTypes;
}

export const MassPublishDialog = (props: MassPublishDialogProps) => {
  const {
    versionIds,
    disabled,
    onSuccess,
    isQuickEditMode,
    entityType = "activity",
    toolType,
  } = props;

  const { mutateAsync: massPublishActivities, isPending: isLoadingActivities } =
    useMassPublishActivityDrafts();
  const { mutateAsync: massPublishTools, isPending: isLoadingTools } =
    useMassPublishToolDrafts();
  const { mutateAsync: massPublishImages, isPending: isLoadingImages } =
    useMassPublishImageDrafts();

  const mutateAsync =
    entityType === "activity"
      ? massPublishActivities
      : entityType === "tool"
        ? massPublishTools
        : massPublishImages;
  const isLoading =
    entityType === "activity"
      ? isLoadingActivities
      : entityType === "tool"
        ? isLoadingTools
        : isLoadingImages;

  const [comment, setComment] = useState<string | undefined>(undefined);
  const [hiddenRevision, setHiddenRevision] = useState<boolean>(false);
  const [publishResults, setPublishResults] =
    useState<MassPublishResponse | null>(null);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const { t } = useTranslation();
  const translationKeys = getMassPublishTranslationKeys(entityType);

  const onTextAreaChange = (
    _ev: ChangeEvent<HTMLTextAreaElement>,
    data: TextareaOnChangeData
  ) => {
    setComment(data.value);
  };

  const onHiddenRevisionChange = (
    _ev: ChangeEvent<HTMLInputElement>,
    data: CheckboxOnChangeData
  ) => {
    setHiddenRevision(data.checked as boolean);
  };

  const onClickPublish = async () => {
    if (!comment) {
      return;
    }

    const request = {
      versionIds: versionIds,
      comment: comment,
      hiddenComment: hiddenRevision,
    };

    try {
      setError(null);
      const response = await mutateAsync(request);

      const hasErrors = response.failedCount > 0;
      if (hasErrors) {
        setPublishResults(response);
        setShowResults(true);
      } else {
        setOpen(false);
        if (onSuccess) {
          onSuccess();
        } else {
          if (entityType === "activity") {
            navigate(ClientRoutes.ActivitiesPublished);
          } else if (entityType === "image") {
            navigate(ClientRoutes.Media);
          } else {
            if (toolType === ToolTypes.Document) {
              navigate(ClientRoutes.Documents);
            } else if (toolType === ToolTypes.Link) {
              navigate(ClientRoutes.Links);
            }
          }
        }
      }
    } catch (error) {
      console.error("Mass publish failed:", error);
      let errorMessage = t(translationKeys.generalError);

      if (error && typeof error === "object" && "message" in error) {
        errorMessage = error.message as string;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      setError(errorMessage);
      setShowResults(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setComment(undefined);
    setHiddenRevision(false);
    setPublishResults(null);
    setShowResults(false);
    setError(null);
  };

  const handleCloseResults = () => {
    setOpen(false);
    setPublishResults(null);
    setShowResults(false);
    setError(null);

    if (onSuccess) {
      onSuccess();
    } else {
      if (entityType === "activity") {
        navigate(ClientRoutes.ActivitiesPublished);
      } else if (entityType === "image") {
        navigate(ClientRoutes.Media);
      } else {
        if (toolType === ToolTypes.Document) {
          navigate(ClientRoutes.Documents);
        } else if (toolType === ToolTypes.Link) {
          navigate(ClientRoutes.Links);
        }
      }
    }
  };

  return (
    <Dialog open={open}>
      <DialogTrigger disableButtonEnhancement>
        <Button
          size="medium"
          disabled={disabled || versionIds.length === 0}
          onClick={() => setOpen(true)}
        >
          {t("massPublish")}
        </Button>
      </DialogTrigger>
      <DialogSurface
        className={classes.dialogSurface}
        aria-describedby={undefined}
      >
        <DialogBody>
          <DialogTitle>
            {t(
              showResults
                ? translationKeys.resultTitle
                : translationKeys.dialogTitle
            )}
          </DialogTitle>
          <DialogContent>
            {showResults ? (
              <div className={classes.content}>
                {error ? (
                  <div className={classes.errorContainer}>
                    <div className={classes.errorTitle}>
                      {t(translationKeys.errorTitle)}
                    </div>
                    <div>{error}</div>
                  </div>
                ) : publishResults && publishResults.results ? (
                  <>
                    <div className={classes.summaryText}>
                      {t(translationKeys.summary, {
                        successful: publishResults.successfulCount,
                        failed: publishResults.failedCount,
                        total: publishResults.totalCount,
                      })}
                    </div>
                    <div className={classes.resultsContainer}>
                      {publishResults.results?.map((result) => (
                        <div
                          key={result.versionId}
                          className={`${classes.resultItem} ${
                            result.success
                              ? classes.successItem
                              : classes.errorItem
                          }`}
                        >
                          {result.success ? (
                            <div className={classes.iconWrapper}>
                              <CheckmarkCircle20Regular
                                className={classes.icon}
                              />
                              {t(translationKeys.itemSuccess, {
                                title: getPublishedItemTitle(
                                  entityType,
                                  result,
                                  t
                                ),
                              })}
                            </div>
                          ) : (
                            <div className={classes.iconWrapper}>
                              <DismissCircle20Regular
                                className={classes.icon}
                              />
                              {t(translationKeys.itemError, {
                                title: result.title || result.versionId,
                                error: getResultErrorMessage(result, t),
                              })}
                              {result.errorCode && (
                                <span>
                                  {" "}
                                  ({t("errorCode")}: {result.errorCode})
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            ) : (
              <div className={classes.content}>
                <div className={classes.countText}>
                  {t(translationKeys.count, { count: versionIds.length })}
                </div>
                <div>
                  {entityType === "activity"
                    ? isQuickEditMode
                      ? t("massPublishingDescriptionQuickEdit")
                      : t(translationKeys.description)
                    : t(translationKeys.description)}
                </div>
                <Label className={classes.commentLabel}>
                  {t("commentLabel")}
                </Label>
                <Textarea
                  value={comment ? comment : ""}
                  onChange={onTextAreaChange}
                  resize="vertical"
                  textarea={{ className: classes.commentTextArea }}
                />
                <Checkbox
                  label={t("hideRevisionCheckboxLabel")}
                  checked={hiddenRevision}
                  onChange={onHiddenRevisionChange}
                />
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {showResults ? (
              <DialogTrigger disableButtonEnhancement>
                <Button onClick={handleCloseResults} appearance="primary">
                  {t("close")}
                </Button>
              </DialogTrigger>
            ) : (
              <>
                <DialogTrigger disableButtonEnhancement>
                  <Button onClick={handleClose} appearance="secondary">
                    {t("close")}
                  </Button>
                </DialogTrigger>
                <DialogTrigger disableButtonEnhancement>
                  <ButtonWithLoading
                    onClick={onClickPublish}
                    isLoading={isLoading}
                    disabled={!comment}
                    text={t("publish")}
                  />
                </DialogTrigger>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
